import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

function TypographyDisplay({ fonts }) {
  const heading = fonts.heading_font || fonts.heading || fonts.primary;
  const body = fonts.body_font || fonts.body || fonts.secondary;
  const accent = fonts.accent_font || fonts.accent;
  return (
    <div>
      {heading && (
        <div className="mb-3">
          <small className="text-muted d-block">Heading Font</small>
          <span style={{ fontSize: '1.5rem', fontFamily: heading }}>{heading}</span>
        </div>
      )}
      {body && (
        <div className="mb-3">
          <small className="text-muted d-block">Body Font</small>
          <span style={{ fontSize: '1.1rem', fontFamily: body }}>{body}</span>
        </div>
      )}
      {accent && (
        <div>
          <small className="text-muted d-block">Accent Font</small>
          <span style={{ fontSize: '1.1rem', fontFamily: accent }}>{accent}</span>
        </div>
      )}
    </div>
  );
}

function BrandKit() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [brandData, setBrandData] = useState(null);
  const [logoData, setLogoData] = useState(null);

  useEffect(() => {
    fetchBrandData();
  }, []);

  const fetchBrandData = async () => {
    setLoading(true);
    try {
      // Fetch complete brand identity
      const response = await fetch('/api/brand/get-complete-brand-identity', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setBrandData(data);

        // Extract logo from the response if it's in selected_shapes
        // We'll also try to fetch it separately
        const logoRes = await fetch('/api/brand/get-saved-logo-direction', {
          credentials: 'include'
        });
        if (logoRes.ok) {
          const logoInfo = await logoRes.json();
          setLogoData(logoInfo);
        }
      } else {
        setError('Failed to load brand data');
      }
    } catch (err) {
      setError('Error loading brand data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading your brand kit...</p>
      </Container>
    );
  }

  const hasAnyBrandData = brandData && (
    brandData.color_palette ||
    brandData.typography ||
    brandData.archetypes?.primary ||
    brandData.brand_voice ||
    brandData.brand_style?.length > 0
  );

  if (!hasAnyBrandData) {
    return (
      <Container className="py-4">
        <h1 className="mb-4">Brand Kit</h1>
        <Alert variant="warning" style={{ backgroundColor: '#fff8e6', borderColor: colors.goldMid }}>
          <Alert.Heading>No Brand Identity Found</Alert.Heading>
          <p>
            Your brand kit will appear here once you complete the Brand Identity section in your Profit Map.
            This includes your colors, fonts, logo direction, and brand voice.
          </p>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={() => navigate('/dashboard/brand-quiz-intro')}
          >
            Set Up Brand Identity
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Brand Kit</h1>
          <p className="text-muted mb-0">
            Quick reference for your brand assets. Use these when creating marketing materials.
          </p>
        </div>
        <Button
          variant="outline-secondary"
          onClick={() => navigate('/dashboard/branding')}
        >
          Edit in Profit Map
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        {/* Color Palette */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Color Palette</h5>
                <Button
                  variant="link"
                  size="sm"
                  style={{ color: colors.goldLight }}
                  onClick={() => navigate('/dashboard/branding')}
                >
                  Edit
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {brandData.color_palette ? (
                <div className="d-flex flex-wrap gap-3">
                  {Object.entries(brandData.color_palette)
                    .filter(([name, color]) => !name.endsWith('_name') && typeof color === 'string' && color.startsWith('#'))
                    .map(([name, color]) => {
                      // Get the display name from the corresponding _name field if it exists
                      const displayName = brandData.color_palette[`${name}_name`] || name;
                      return (
                        <div
                          key={name}
                          className="text-center"
                          style={{ cursor: 'pointer' }}
                          onClick={() => copyToClipboard(color)}
                          title="Click to copy hex code"
                        >
                          <div
                            style={{
                              width: '60px',
                              height: '60px',
                              backgroundColor: color,
                              borderRadius: '8px',
                              border: '2px solid #dee2e6',
                              marginBottom: '4px'
                            }}
                          />
                          <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>
                            {displayName}
                          </small>
                          <small className="d-block" style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                            {color}
                          </small>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-muted mb-0">No colors defined yet</p>
              )}
              <small className="text-muted d-block mt-3">Click any color to copy hex code</small>
            </Card.Body>
          </Card>
        </Col>

        {/* Typography */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Typography</h5>
                <Button
                  variant="link"
                  size="sm"
                  style={{ color: colors.goldLight }}
                  onClick={() => navigate('/dashboard/branding')}
                >
                  Edit
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {brandData.typography ? (
                <TypographyDisplay fonts={brandData.typography} />
              ) : (
                <p className="text-muted mb-0">No fonts defined yet</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Brand Archetypes */}
        <Col lg={4} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Brand Archetypes</h5>
                <Button
                  variant="link"
                  size="sm"
                  style={{ color: colors.goldLight }}
                  onClick={() => navigate('/dashboard/branding')}
                >
                  Edit
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {brandData.archetypes?.primary ? (
                <div>
                  <div className="mb-3">
                    <Badge bg="primary" className="me-2">Primary</Badge>
                    <strong>{brandData.archetypes.primary.name}</strong>
                    <small className="text-muted ms-2">({Math.round(brandData.archetypes.primary.score)}%)</small>
                  </div>
                  {brandData.archetypes.secondary && (
                    <div className="mb-3">
                      <Badge bg="secondary" className="me-2">Secondary</Badge>
                      <strong>{brandData.archetypes.secondary.name}</strong>
                      <small className="text-muted ms-2">({Math.round(brandData.archetypes.secondary.score)}%)</small>
                    </div>
                  )}
                  {brandData.archetypes.tertiary && (
                    <div>
                      <Badge bg="light" text="dark" className="me-2">Tertiary</Badge>
                      <strong>{brandData.archetypes.tertiary.name}</strong>
                      <small className="text-muted ms-2">({Math.round(brandData.archetypes.tertiary.score)}%)</small>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted mb-0">No archetypes defined yet</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Brand Voice */}
        <Col lg={8} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Brand Voice</h5>
                <Button
                  variant="link"
                  size="sm"
                  style={{ color: colors.goldLight }}
                  onClick={() => navigate('/dashboard/branding')}
                >
                  Edit
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {brandData.brand_voice ? (
                <p style={{ whiteSpace: 'pre-wrap' }}>{brandData.brand_voice}</p>
              ) : (
                <p className="text-muted mb-0">No brand voice defined yet</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Brand Style Words */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Brand Style</h5>
                <Button
                  variant="link"
                  size="sm"
                  style={{ color: colors.goldLight }}
                  onClick={() => navigate('/dashboard/branding')}
                >
                  Edit
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {brandData.brand_style && brandData.brand_style.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {brandData.brand_style.map((word, i) => (
                    <Badge
                      key={i}
                      bg="light"
                      text="dark"
                      style={{ fontSize: '0.9rem', padding: '8px 12px' }}
                    >
                      {word}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0">No style words defined yet</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Brand Texture */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Brand Texture</h5>
                <Button
                  variant="link"
                  size="sm"
                  style={{ color: colors.goldLight }}
                  onClick={() => navigate('/dashboard/branding')}
                >
                  Edit
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {brandData.brand_texture && brandData.brand_texture.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {brandData.brand_texture.map((word, i) => (
                    <Badge
                      key={i}
                      bg="light"
                      text="dark"
                      style={{ fontSize: '0.9rem', padding: '8px 12px' }}
                    >
                      {word}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0">No texture words defined yet</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Mood Board */}
      {brandData.mood_board && brandData.mood_board.length > 0 && (
        <Row>
          <Col>
            <Card className="shadow-sm mb-4">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Mood Board</h5>
                  <Button
                    variant="link"
                    size="sm"
                    style={{ color: colors.goldLight }}
                    onClick={() => navigate('/dashboard/branding')}
                  >
                    Edit
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="d-flex flex-wrap gap-3">
                  {brandData.mood_board.map((img, i) => (
                    <img
                      key={i}
                      src={img.url || img}
                      alt={`Mood board ${i + 1}`}
                      style={{
                        width: '150px',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #dee2e6'
                      }}
                    />
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Icon Images */}
      {brandData.icon_images && brandData.icon_images.length > 0 && (
        <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Brand Marker</h5>
                  <Button
                    variant="link"
                    size="sm"
                    style={{ color: colors.goldLight }}
                    onClick={() => navigate('/dashboard/branding')}
                  >
                    Edit
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {brandData.iconography && (
                  <p className="mb-3">
                    <strong>Style:</strong> {typeof brandData.iconography === 'string' ? brandData.iconography : (brandData.iconography.visual_style?.direction || brandData.iconography.overview || 'Custom iconography')}
                  </p>
                )}
                <div className="d-flex flex-wrap gap-3">
                  {brandData.icon_images.map((img, i) => (
                    <img
                      key={i}
                      src={typeof img === 'string' ? img : img.url}
                      alt={`Icon ${i + 1}`}
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'contain',
                        padding: '8px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                      }}
                    />
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default BrandKit;
